import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Login: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'register_mine'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedMine, setSelectedMine] = useState('');
  const [selectedRole, setSelectedRole] = useState('site_user');
  const [mines, setMines] = useState<{id: string; name: string; state: string}[]>([]);
  // Mine registration fields
  const [mineName, setMineName] = useState('');
  const [mineOperator, setMineOperator] = useState('');
  const [mineState, setMineState] = useState('NSW');
  const [mineType, setMineType] = useState('underground_coal');
  const [eemName, setEemName] = useState('');
  const [eemEmail, setEemEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const { data } = await api.post('/api/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('psf_token', data.access_token);
      storage.setItem('psf_user', JSON.stringify({
        id: data.user_id, name: data.name, role: data.role,
      }));
      // Also set in localStorage so the app can find it
      localStorage.setItem('psf_token', data.access_token);
      localStorage.setItem('psf_user', JSON.stringify({
        id: data.user_id, name: data.name, role: data.role,
      }));
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/auth/register', {
        email, password, name,
        organisation_id: selectedMine || null,
        requested_role: selectedRole,
      });
      setSuccess('Access request submitted! Your site administrator will review your request.');
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSuccess('If that email exists, a reset link has been sent.');
    } catch (err: any) {
      // Always show success to prevent email enumeration
      setSuccess('If that email exists, a reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleMineRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (password !== confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }

    try {
      // This creates the mine + EEM account in one go
      // For now, uses the public registration endpoint which JMB admin will approve
      await api.post('/api/auth/register-mine', {
        mine_name: mineName,
        operator: mineOperator || null,
        state: mineState,
        mine_type: mineType,
        eem_name: eemName,
        eem_email: eemEmail,
        eem_password: password,
      });
      setSuccess('Mine registration submitted! JMB Engineering will review and activate your site. You\'ll receive an email once approved.');
      setMode('login');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = async (newMode: 'login' | 'register' | 'forgot' | 'register_mine') => {
    setMode(newMode);
    setError('');
    setSuccess('');
    if (newMode === 'register' && mines.length === 0) {
      try {
        const { data } = await api.get('/api/admin/public/mines');
        setMines(data);
      } catch (err) { /* mines list will be empty */ }
    }
  };

  return (
    <div style={styles.container}>
      {/* Gradient accent line at top */}
      <div style={styles.accentLine} />

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoSection}>
          <img src="/logo-white.png" alt="JMB Engineering" style={styles.logo} />
          <div style={styles.divider} />
          <h1 style={styles.title}>Plant Safety File Platform</h1>
          <p style={styles.subtitle}>
            {mode === 'login' && 'Sign in to manage your plant safety files'}
            {mode === 'register' && 'Request access to your site'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'register_mine' && 'Register your mine or site'}
          </p>
        </div>

        {/* Messages */}
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                style={styles.input} placeholder="you@example.com" required autoFocus
              />
            </div>
            <div style={styles.field}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={styles.label}>Password</label>
                <button type="button" onClick={() => switchMode('forgot')} style={styles.forgotLink}>
                  Forgot password?
                </button>
              </div>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                style={styles.input} placeholder="••••••••" required
              />
            </div>
            <label style={styles.rememberRow}>
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={styles.checkbox} />
              <span style={styles.rememberText}>Remember me</span>
            </label>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <p style={styles.switchText}>
              Don't have an account?{' '}
              <button type="button" onClick={() => switchMode('register')} style={styles.switchLink}>
                Request access
              </button>
            </p>
          </form>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Full Name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                style={styles.input} placeholder="John Smith" required autoFocus
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                style={styles.input} placeholder="you@example.com" required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mine / Site</label>
              <select value={selectedMine} onChange={(e) => setSelectedMine(e.target.value)} style={styles.input} required>
                <option value="">Select your mine or site...</option>
                {mines.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.state})</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Role</label>
              <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} style={styles.input}>
                <option value="site_user">Site User — View & upload documents</option>
                <option value="site_admin">Site Admin — Manage site users & equipment</option>
                <option value="eem">EEM — Electrical Engineering Manager</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                style={styles.input} placeholder="Min 8 characters" required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.input} placeholder="••••••••" required
              />
            </div>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Submitting request...' : 'Request Access'}
            </button>
            <p style={styles.switchText}>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('login')} style={styles.switchLink}>
                Sign in
              </button>
            </p>
            <p style={{ fontSize: '11px', color: '#868e96', textAlign: 'center' as const, lineHeight: '1.5' }}>
              Your request will be reviewed by your site administrator, then approved by JMB Engineering.
            </p>
            <p style={styles.switchText}>
              Mine not listed?{' '}
              <button type="button" onClick={() => switchMode('register_mine')} style={styles.switchLink}>
                Register your mine
              </button>
            </p>
          </form>
        )}

        {/* Mine Registration Form */}
        {mode === 'register_mine' && (
          <form onSubmit={handleMineRegister} style={styles.form}>
            <p style={{ fontSize: '13px', color: '#495057', lineHeight: '1.5', margin: '0 0 8px' }}>
              Register your mine to start managing plant safety files. You'll be set up as the site's EEM (master administrator).
            </p>
            <div style={styles.field}>
              <label style={styles.label}>Mine / Site Name</label>
              <input type="text" value={mineName} onChange={(e) => setMineName(e.target.value)}
                style={styles.input} placeholder="e.g. Springvale Mine" required autoFocus />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Operator</label>
              <input type="text" value={mineOperator} onChange={(e) => setMineOperator(e.target.value)}
                style={styles.input} placeholder="e.g. Centennial Coal" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={styles.field}>
                <label style={styles.label}>State</label>
                <select value={mineState} onChange={(e) => setMineState(e.target.value)} style={styles.input}>
                  <option value="NSW">NSW</option>
                  <option value="QLD">QLD</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Type</label>
                <select value={mineType} onChange={(e) => setMineType(e.target.value)} style={styles.input}>
                  <option value="underground_coal">Underground Coal</option>
                  <option value="surface_coal">Surface Coal</option>
                  <option value="metalliferous">Metalliferous</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div style={{ height: '1px', background: '#e9ecef', margin: '8px 0' }} />
            <p style={{ fontSize: '11px', color: '#868e96', margin: '0 0 4px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
              Your Details (EEM / Site Administrator)
            </p>
            <div style={styles.field}>
              <label style={styles.label}>Full Name</label>
              <input type="text" value={eemName} onChange={(e) => setEemName(e.target.value)}
                style={styles.input} placeholder="Your full name" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={eemEmail} onChange={(e) => setEemEmail(e.target.value)}
                style={styles.input} placeholder="you@example.com" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                style={styles.input} placeholder="Min 8 characters" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.input} placeholder="••••••••" required />
            </div>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Submitting...' : 'Register Mine'}
            </button>
            <p style={styles.switchText}>
              <button type="button" onClick={() => switchMode('login')} style={styles.switchLink}>
                ← Back to sign in
              </button>
            </p>
            <p style={{ fontSize: '11px', color: '#868e96', textAlign: 'center' as const, lineHeight: '1.5' }}>
              JMB Engineering will review your registration and set up your site. You'll receive an email once activated.
            </p>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgot} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                style={styles.input} placeholder="you@example.com" required autoFocus
              />
            </div>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <p style={styles.switchText}>
              <button type="button" onClick={() => switchMode('login')} style={styles.switchLink}>
                ← Back to sign in
              </button>
            </p>
          </form>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerDivider} />
          <p style={styles.footerText}>JMB Engineering</p>
          <p style={styles.footerSubtext}>Engineering. Excellence. Delivered.</p>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #2D2D2D 0%, #3C3C3C 40%, #4A4A4A 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    position: 'relative' as const,
  },
  accentLine: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #116dff 0%, #B0B8C0 50%, #116dff 100%)',
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '0',
    width: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    overflow: 'hidden',
  },
  logoSection: {
    background: 'linear-gradient(135deg, #2D2D2D 0%, #3C3C3C 40%, #4A4A4A 100%)',
    padding: '36px 40px 28px',
    textAlign: 'center' as const,
  },
  logo: {
    height: '52px',
    marginBottom: '16px',
    filter: 'brightness(1.1)',
  },
  divider: {
    width: '60px',
    height: '2px',
    background: 'linear-gradient(90deg, #116dff 0%, #B0B8C0 50%, #116dff 100%)',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff',
    letterSpacing: '0.5px',
  },
  subtitle: {
    margin: '6px 0 0',
    fontSize: '13px',
    color: '#B0B8C0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '18px',
    padding: '28px 40px 8px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3C3C3C',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  },
  input: {
    padding: '12px 14px',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#212529',
  },
  rememberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    marginTop: '-4px',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    accentColor: '#116dff',
  },
  rememberText: {
    fontSize: '13px',
    color: '#495057',
  },
  forgotLink: {
    background: 'none',
    border: 'none',
    color: '#116dff',
    fontSize: '12px',
    cursor: 'pointer',
    padding: 0,
    fontWeight: 500,
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(135deg, #2D2D2D 0%, #3C3C3C 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.5px',
    marginTop: '4px',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  switchText: {
    textAlign: 'center' as const,
    fontSize: '13px',
    color: '#868e96',
    margin: '4px 0 0',
  },
  switchLink: {
    background: 'none',
    border: 'none',
    color: '#116dff',
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
    fontWeight: 600,
  },
  error: {
    background: '#fff0f0',
    color: '#d32f2f',
    padding: '10px 14px',
    fontSize: '13px',
    margin: '0 40px',
    borderRadius: '6px',
    marginTop: '20px',
  },
  success: {
    background: '#f0fff4',
    color: '#2e7d32',
    padding: '10px 14px',
    fontSize: '13px',
    margin: '0 40px',
    borderRadius: '6px',
    marginTop: '20px',
  },
  footer: {
    padding: '20px 40px 24px',
    textAlign: 'center' as const,
  },
  footerDivider: {
    width: '40px',
    height: '1px',
    background: '#e9ecef',
    margin: '0 auto 12px',
  },
  footerText: {
    margin: '0',
    fontSize: '12px',
    color: '#868e96',
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
  },
  footerSubtext: {
    margin: '6px 0 0',
    fontSize: '11px',
    color: '#B0B8C0',
    letterSpacing: '4px',
    textTransform: 'uppercase' as const,
    fontWeight: 600,
  },
};

export default Login;
